/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { NavigateFunction } from 'react-router-dom';

// Exact return targets allowed for the SSO token hand-off (origin + path pinned).
export const SSO_ALLOWED_RETURNS = [
  'https://inference.podstack.ai/inference/auth/callback',
  'https://training.podstack.ai/training/auth/callback',
];

export function isAllowedReturn(url: string): boolean {
  try {
    const u = new URL(url);
    return SSO_ALLOWED_RETURNS.includes(u.origin + u.pathname);
  } catch {
    return false;
  }
}

// Single post-login finisher: if a pending invitation token was stashed
// (login-first invitation acceptance), return to the accept-invitation page
// first. Otherwise, if an allowlisted SSO return URL was stashed, hand the
// JWT over in the URL FRAGMENT (never the query string, so it is not sent to
// servers or logged); otherwise navigate home as before.
export function finishLogin(navigate: NavigateFunction) {
  // CLI sign-in hand-off: if login was started from `podstack auth login`,
  // return to the CLI authorize page to complete the token hand-off. This is
  // an internal same-origin path, so it is navigated (not an external return).
  const cliReturn = sessionStorage.getItem('cli_return');
  if (cliReturn && cliReturn.startsWith('/cli/authorize')) {
    sessionStorage.removeItem('cli_return');
    navigate(cliReturn);
    return;
  }

  const pendingInvite = sessionStorage.getItem('pending_invitation_token');
  if (pendingInvite) {
    sessionStorage.removeItem('pending_invitation_token');
    navigate(`/invitations/accept?token=${pendingInvite}`);
    return;
  }

  const ret = sessionStorage.getItem('sso_return');
  if (ret && isAllowedReturn(ret)) {
    sessionStorage.removeItem('sso_return');
    const token = localStorage.getItem('token') || '';
    const u = new URL(ret);
    u.hash = 'sso_token=' + encodeURIComponent(token);
    window.location.href = u.toString();
    return;
  }
  navigate('/');
}
